'use client';

import * as React from 'react';
import { cn } from '@/utils/cn';

interface SectionEntryRevealProps {
  children: React.ReactNode;
  className?: string;
  itemClassName?: string;
  rootMargin?: string;
  threshold?: number;
  staggerMs?: number;
}

export default function SectionEntryReveal({
  children,
  className,
  itemClassName,
  rootMargin = '0px 0px -15% 0px',
  threshold = 0.2,
  staggerMs = 110,
}: SectionEntryRevealProps) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    const element = ref.current;
    if (!element || isVisible) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setIsVisible(true);
        observer.disconnect();
      },
      { rootMargin, threshold },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [isVisible, rootMargin, threshold]);

  return (
    <div ref={ref} className={className}>
      {React.Children.map(children, (child, index) => (
        <div
          className={cn(
            'transform-gpu transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:translate-y-0 motion-reduce:opacity-100',
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0',
            itemClassName,
          )}
          style={{ transitionDelay: isVisible ? `${index * staggerMs}ms` : '0ms' }}
        >
          {child}
        </div>
      ))}
    </div>
  );
}
